import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil, distinctUntilChanged, filter } from 'rxjs';
import { SurveyService, Survey, Question, SurveyResponse } from '../../../services/survey.service';
import { AuthService } from '../../../auth/auth.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import * as confetti from 'canvas-confetti';

@Component({
  selector: 'survey-widget',
  templateUrl: './survey-widget.component.html',
  styleUrls: ['./survey-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyWidgetComponent implements OnInit, OnDestroy {
  private unsubscribe$: Subject<void>;
  private userId: string | null = null;
  private hasTrackedView = false;

  public currentSurvey: Survey | null = null;
  public currentQuestion: Question | null = null;
  public showSurvey = false;
  public isSingleQuestionSurvey = false;
  public surveyQuestions: Question[] = [];
  public surveyReady = false;

  // Survey flow states
  public surveyState: 'question' | 'thank-you' = 'question';
  public selectedAnswer: string = '';
  public additionalFeedback: string = '';
  public currentResponse: SurveyResponse | null = null;
  public isSubmitting = false;

  // Auto-dismiss timer properties
  public autoCloseTimer: any = null;
  public autoCloseTimeLeft = 7.5;
  public autoCloseProgress = 100;
  public progressBarVisible = true;
  private readonly AUTO_CLOSE_DELAY = 7500; // 7.5 seconds

  // Question types and parsed options
  public questionOptions: string[] = [];

  // Multi-question support
  public currentQuestionIndex = 0;
  public totalQuestions = 0;
  public isTransitioning = false;

  constructor(
    private surveyService: SurveyService,
    private authService: AuthService,
    private changeRef: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {
    this.unsubscribe$ = new Subject();
  }

  public ngOnInit() {
    // Get current user ID (may be null initially if session is refreshing)
    this.userId = this.authService.getCognitoUserId();

    // Always set up subscriptions regardless of userId availability.
    // userId is only needed for dismiss/submit actions, not for displaying the survey.
    // Previously, returning early here when userId was null caused surveys to never
    // render for users whose Cognito session was being refreshed during ngOnInit.

    // Subscribe to survey state with distinctUntilChanged to prevent duplicate updates
    this.surveyService.showSurvey$.pipe(
      takeUntil(this.unsubscribe$),
      distinctUntilChanged()
    ).subscribe((show) => {
      this.showSurvey = show;
      this.changeRef.markForCheck();
      this.trackViewIfReady();
    });

    this.surveyService.currentSurvey$.pipe(
      takeUntil(this.unsubscribe$),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id)
    ).subscribe((survey) => {
      this.currentSurvey = survey;
      this.resetSurveyState();
      this.changeRef.markForCheck();
      this.trackViewIfReady();
    });

    this.surveyService.currentQuestion$.pipe(
      takeUntil(this.unsubscribe$),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
      filter(question => question !== null)
    ).subscribe((question) => {
      this.currentQuestion = question;
      if (question) {
        this.questionOptions = this.surveyService.getParsedOptions(question);
      }
      this.surveyReady = true;
      this.changeRef.markForCheck();
      this.trackViewIfReady();
    });

    // Subscribe to questions array for progress tracking
    this.surveyService.questions$.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe((questions) => {
      this.surveyQuestions = questions;
      this.totalQuestions = questions.length;
      this.isSingleQuestionSurvey = questions.length === 1;
      this.changeRef.markForCheck();
    });

    // Subscribe to current question index
    this.surveyService.currentQuestionIndex$.pipe(
      takeUntil(this.unsubscribe$)
    ).subscribe((index) => {
      this.currentQuestionIndex = index;
      this.changeRef.markForCheck();
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.clearAutoCloseTimer();
  }

  private resetSurveyState() {
    this.surveyState = 'question';
    this.selectedAnswer = '';
    this.additionalFeedback = '';
    this.currentResponse = null;
    this.isSubmitting = false;
    this.questionOptions = [];
    this.isSingleQuestionSurvey = false;
    this.surveyQuestions = [];
    this.surveyReady = false;
    this.progressBarVisible = true;
    this.currentQuestionIndex = 0;
    this.totalQuestions = 0;
    this.isTransitioning = false;
    this.hasTrackedView = false;
    this.clearAutoCloseTimer();
  }

  /**
   * Track survey view only after all rendering conditions are met.
   * Fires exactly once per survey — ensures VIEWED is only recorded
   * when the user can actually see the survey widget.
   */
  private trackViewIfReady(): void {
    if (
      !this.hasTrackedView &&
      this.showSurvey &&
      this.currentSurvey &&
      this.currentQuestion &&
      this.surveyReady
    ) {
      this.hasTrackedView = true;
      this.surveyService.trackSurveyView(this.currentSurvey.id);
      this.analyticsService.trackWidgetInteraction('survey', 'viewed', { surveyId: this.currentSurvey.id });
    }
  }

  public onAnswerChange(answer: string) {
    this.selectedAnswer = answer;

    // Auto-advance for multiple choice/rating in multi-question surveys
    if (this.shouldAutoAdvance()) {
      this.onSubmitAnswer();
    }
  }

  /**
   * Check if we should auto-advance after answer selection
   * Only for multiple choice/rating questions in multi-question surveys
   */
  private shouldAutoAdvance(): boolean {
    if (!this.currentQuestion?.type || this.totalQuestions <= 1) {
      return false;
    }

    const questionType = this.currentQuestion.type.toUpperCase();
    return questionType === 'MULTIPLE_CHOICE' || questionType === 'RATING';
  }

  /**
   * Check if submit button should be shown
   * Hidden for multiple choice/rating in multi-question surveys (auto-advances)
   */
  public showSubmitButton(): boolean {
    return !this.shouldAutoAdvance();
  }

  public async onSubmitAnswer() {
    // Lazily resolve userId if it wasn't available during ngOnInit (session refresh)
    if (!this.userId) {
      this.userId = this.authService.getCognitoUserId();
    }
    if (!this.userId || !this.selectedAnswer || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.changeRef.detectChanges();

    try {
      const response = await this.surveyService.submitResponse(this.userId, this.selectedAnswer);

      if (response) {
        this.currentResponse = response;

        // Track survey question answered
        if (this.currentSurvey) {
          this.analyticsService.trackWidgetInteraction('survey', 'answered', {
            surveyId: this.currentSurvey.id,
            questionIndex: this.currentQuestionIndex,
            totalQuestions: this.totalQuestions
          });
        }

        // Check if this was the last question
        if (this.surveyService.isLastQuestion()) {
          // Show thank-you state
          this.surveyState = 'thank-you';
          this.startAutoCloseTimer();
          this.triggerConfetti();
        } else {
          // Fade out, advance to next question, fade in
          this.isTransitioning = true;
          this.changeRef.detectChanges();

          setTimeout(() => {
            this.surveyService.advanceToNextQuestion();
            this.selectedAnswer = ''; // Reset answer for next question
            this.isTransitioning = false;
            this.isSubmitting = false;
            this.changeRef.detectChanges();
          }, 300); // Match CSS transition duration
          return; // Exit early since we're handling isSubmitting in setTimeout
        }
      } else {
        // console.error('Failed to submit survey answer');
        // Could show error toast here
      }
    } catch (error) {
      // console.error('Error submitting survey answer:', error);
    } finally {
      this.isSubmitting = false;
      this.changeRef.detectChanges();
    }
  }

  public async onSubmitFeedback() {
    if (!this.currentResponse || this.isSubmitting) {
      // If no additional feedback, just close the survey
      if (!this.additionalFeedback.trim()) {
        this.onCloseSurvey();
        return;
      }
    }

    this.isSubmitting = true;
    this.changeRef.detectChanges();

    try {
      if (this.additionalFeedback.trim()) {
        const success = await this.surveyService.updateResponseNotes(
          this.currentResponse!.id, 
          this.additionalFeedback
        );
        
        if (success) {
          // console.log('Survey feedback submitted successfully');
        } else {
          // console.error('Failed to submit survey feedback');
        }
      }
    } catch (error) {
      // console.error('Error submitting survey feedback:', error);
    } finally {
      this.isSubmitting = false;
      this.onCloseSurvey();
    }
  }

  public onCloseSurvey() {
    // Lazily resolve userId if it wasn't available during ngOnInit
    if (!this.userId) {
      this.userId = this.authService.getCognitoUserId();
    }

    // If we haven't submitted an answer yet, treat this as a dismissal
    if (this.surveyState === 'question') {
      // Track survey dismissed
      if (this.currentSurvey) {
        this.analyticsService.trackWidgetInteraction('survey', 'dismissed', { surveyId: this.currentSurvey.id });
      }
      if (this.userId) {
        this.surveyService.dismissCurrentSurvey(this.userId);
      } else {
        this.surveyService.hideSurvey();
      }
    } else {
      // Survey was completed, just hide it
      this.surveyService.hideSurvey();
    }
  }

  public isValidAnswer(): boolean {
    if (!this.currentQuestion || !this.currentQuestion.type) {
      return false;
    }

    const questionType = this.currentQuestion.type.toUpperCase();
    switch (questionType) {
      case 'MULTIPLE_CHOICE':
      case 'RATING':
      case 'THUMBS':
        return this.selectedAnswer.length > 0;
      case 'TEXT':
        return this.selectedAnswer.trim().length > 0;
      case 'NUMBER':
        return !isNaN(parseFloat(this.selectedAnswer));
      default:
        return this.selectedAnswer.length > 0;
    }
  }

  private startAutoCloseTimer() {
    this.clearAutoCloseTimer();
    this.autoCloseTimeLeft = 7.5;
    this.autoCloseProgress = 100;
    
    const interval = 100; // Update every 100ms for smooth progress
    const decrementPerInterval = (interval / this.AUTO_CLOSE_DELAY) * 100;
    
    this.autoCloseTimer = setInterval(() => {
      this.autoCloseProgress -= decrementPerInterval;
      this.autoCloseTimeLeft = Math.ceil(this.autoCloseProgress / 13.333); // Convert progress to seconds (100/7.5)
      
      if (this.autoCloseProgress <= 0) {
        this.clearAutoCloseTimer();
        this.onCloseSurvey();
      }
      
      this.changeRef.detectChanges();
    }, interval);
  }

  private clearAutoCloseTimer() {
    if (this.autoCloseTimer) {
      clearInterval(this.autoCloseTimer);
      this.autoCloseTimer = null;
      this.autoCloseTimeLeft = 7.5;
      this.autoCloseProgress = 100;
    }
  }

  public onFeedbackFocus() {
    // Stop the auto-close timer and fade out progress bar when user starts typing
    this.clearAutoCloseTimer();
    this.progressBarVisible = false;
    this.changeRef.detectChanges();
  }

  private triggerConfetti() {
    const duration = 750;
    const end = Date.now() + duration;
    const confettiFn = (confetti as any).default || confetti;

    const frame = () => {
      confettiFn({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 }
      });
      confettiFn({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 }
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }
}